import React, { ReactNode, FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CellContext,
  ColumnHelper,
  defineColumns,
  RowSelectionState,
  TanstackTable,
} from '@thingslog/ui-components';
import { Typography } from '@mui/material';
import { useViewport } from '../../../hooks';

export const DeviceSelector: FC<DevicesTableProps> = ({
  data,
  selectedRows,
  onDevicesSelect,
  isLoading,
}: DevicesTableProps) => {
  const { t } = useTranslation();
  const isMobile = useViewport('sm');

  const rowSelection = useMemo<RowSelectionState>(
    () => Object.fromEntries(selectedRows.map((id: string) => [id, true])),
    [selectedRows]
  );

  const columns = useMemo(
    () =>
      defineColumns<TableDataItem>((column: ColumnHelper<TableDataItem>) => {
        const boldHeader = (label: string): ReactNode => (
          <Typography variant={isMobile ? 'body2' : 'body1'} className="font-semibold">
            {label}
          </Typography>
        );
        const bodyText = (value: ReactNode): ReactNode => (
          <Typography variant={isMobile ? 'body2' : 'body1'} className="text-gray-800">
            {value}
          </Typography>
        );

        return [
          column.accessor('number', {
            header: (): ReactNode => boldHeader(t('alarms_export_table_device_number')),
            size: 136,
            meta: {
              filterVariant: 'text',
              label: t('alarms_export_table_device_number'),
              minWidth: 112,
            },
            cell: (info: CellContext<TableDataItem, string>): ReactNode =>
              bodyText(info.getValue()),
          }),
          column.accessor('name', {
            header: (): ReactNode => boldHeader(t('alarms_export_table_device_name')),
            meta: {
              filterVariant: 'text',
              label: t('alarms_export_table_device_name'),
              grow: true,
            },
            cell: (info: CellContext<TableDataItem, string>): ReactNode =>
              bodyText(info.getValue()),
          }),
        ];
      }),
    [t, isMobile]
  );

  return (
    <TanstackTable<TableDataItem>
      mode="client"
      className="h-full w-full"
      data={data}
      columns={columns}
      getRowId={(row: TableDataItem): string => row.number}
      isLoading={isLoading}
      density="compact"
      enableRowSelection
      enablePagination={false}
      showToolbar={false}
      enableColumnFilters={false}
      stickyHeader
      classNames={{ container: 'max-h-full' }}
      emptyMessage={t('task_scheduler_no_devices', 'No devices')}
      state={{ rowSelection }}
      onRowSelectionChange={(selection: RowSelectionState): void =>
        onDevicesSelect(Object.keys(selection).filter((id: string) => selection[id]))
      }
    />
  );
};

interface DevicesTableProps {
  data: TableDataItem[];
  selectedRows: string[];
  onDevicesSelect: (ids: string[]) => void;
  isLoading?: boolean;
}

export interface TableDataItem {
  number: string;
  name: string;
}
